import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, TableShell, Field, Drawer } from "@/components/ui-kit";
import DemoTenantDrawer from "@/components/DemoTenantDrawer";
import { toast } from "sonner";

const STATUS_DEFS = [
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "demo_scheduled", label: "Demo scheduled" },
  { key: "trial", label: "Trial" },
  { key: "won", label: "Won" },
  { key: "lost", label: "Lost" },
];
const STATUS_OPTS = STATUS_DEFS.map((s) => ({ value: s.key, label: s.label }));
const FLEET_OPTS = [
  { value: "", label: "Select fleet size" },
  { value: "1 to 3", label: "1 to 3" },
  { value: "4 to 10", label: "4 to 10" },
  { value: "11 to 20", label: "11 to 20" },
  { value: "more than 20", label: "more than 20" },
];

const EMPTY_COUNTS = { new: 0, contacted: 0, demo_scheduled: 0, trial: 0, won: 0, lost: 0 };
const EMPTY_FORM = { name: "", company: "", email: "", phone: "", fleet_size: "", current_tools: "", message: "" };
const TOOLS_TRUNCATE = 60;

function fmtWhen(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function toDatetimeLocalValue(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function Prospects() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [notesOpen, setNotesOpen] = useState(false);
  const [notesRow, setNotesRow] = useState(null);
  const [notesDraft, setNotesDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  const [demoOpen, setDemoOpen] = useState(false);
  const [demoProspect, setDemoProspect] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (statusFilter) params.status = statusFilter;
    return api
      .get("/demo-requests", { params })
      .then((r) => {
        setRows(r.data?.rows || []);
        setCounts(r.data?.counts || EMPTY_COUNTS);
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const mergeRow = (updated) => {
    setRows((rs) => rs.map((r) => (r.id === updated.id ? updated : r)));
  };

  const toggleTile = (key) => {
    setStatusFilter((cur) => (cur === key ? "" : key));
  };

  const saveProspect = async () => {
    setSaving(true);
    try {
      await api.post("/demo-requests", { ...form, source: "manual" });
      toast.success("Prospect added");
      setAddOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/demo-requests/${id}`, { status });
      toast.success("Status updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const updateDemoAt = async (id, value) => {
    try {
      const { data } = await api.patch(`/demo-requests/${id}`, { demo_at: value });
      mergeRow(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openNotes = (row) => {
    setNotesRow(row);
    setNotesDraft(row.notes || "");
    setNotesOpen(true);
  };

  const openDemo = (row) => {
    setDemoProspect(row);
    setDemoOpen(true);
  };

  const fmtDateOnly = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleDateString();
  };

  const saveNotes = async () => {
    if (!notesRow) return;
    setSavingNotes(true);
    try {
      const { data } = await api.patch(`/demo-requests/${notesRow.id}`, { notes: notesDraft });
      toast.success("Notes saved");
      mergeRow(data);
      setNotesRow(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingNotes(false);
    }
  };

  const total = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <div data-testid="prospects-page">
      <PageHeader
        subtitle="Demo requests and sales pipeline"
        title="Prospects"
        action={
          <button
            onClick={() => {
              setForm(EMPTY_FORM);
              setAddOpen(true);
            }}
            className="btn-primary"
            data-testid="prospect-add"
          >
            + Add prospect
          </button>
        }
      />

      <div className="text-sm text-slate-600 mb-4">{total} total prospect{total === 1 ? "" : "s"}</div>

      <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-5">
        {STATUS_DEFS.map((s) => {
          const active = statusFilter === s.key;
          return (
            <button
              key={s.key}
              onClick={() => toggleTile(s.key)}
              className={`text-left rounded-md border p-3 transition ${
                active ? "bg-[#0A192F] border-[#0A192F]" : "bg-white border-slate-200 hover:border-slate-400"
              }`}
              data-testid={`prospect-tile-${s.key}`}
            >
              <div className={`text-[10px] font-semibold uppercase tracking-[0.1em] ${active ? "text-slate-300" : "text-slate-500"}`}>
                {s.label}
              </div>
              <div className={`font-display text-xl font-bold mt-1 ${active ? "text-white" : "text-[#0A192F]"}`}>
                {counts[s.key] ?? 0}
              </div>
            </button>
          );
        })}
      </div>

      <TableShell
        columns={["When", "Name / Company", "Contact", "Fleet", "Uses today", "Source", "Status", "Demo", "Notes"]}
        empty={
          !loading && rows.length === 0
            ? "No prospects yet. Website requests appear here automatically; use Add prospect for people you meet elsewhere."
            : null
        }
      >
        {rows.map((p) => {
          const toolsFull = p.current_tools || "";
          const toolsShown = toolsFull.length > TOOLS_TRUNCATE ? `${toolsFull.slice(0, TOOLS_TRUNCATE)}...` : toolsFull;
          return (
            <tr key={p.id} className="tbl-row border-t border-slate-100" data-testid={`prospect-row-${p.id}`}>
              <td className="px-4 py-2.5 font-mono text-xs whitespace-nowrap">{fmtWhen(p.at)}</td>
              <td className="px-4 py-2.5">
                <div className="font-medium text-slate-900">{p.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">{p.company || "-"}</div>
              </td>
              <td className="px-4 py-2.5 text-xs">
                {p.email ? (
                  <a href={`mailto:${p.email}`} className="text-[#005E82] hover:underline">
                    {p.email}
                  </a>
                ) : (
                  <span className="text-slate-400">-</span>
                )}
                <div className="text-slate-500 mt-0.5">{p.phone || ""}</div>
              </td>
              <td className="px-4 py-2.5 text-xs">{p.fleet_size || "-"}</td>
              <td className="px-4 py-2.5 max-w-[220px]">
                <span className="text-xs text-slate-600" title={toolsFull || undefined}>
                  {toolsShown || "-"}
                </span>
              </td>
              <td className="px-4 py-2.5">
                <span
                  className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full border ${
                    p.source === "landing" ? "bg-sky-50 text-sky-800 border-sky-200" : "bg-slate-50 text-slate-600 border-slate-200"
                  }`}
                >
                  {p.source === "landing" ? "Website" : "Manual"}
                </span>
              </td>
              <td className="px-4 py-2.5">
                <select
                  value={p.status}
                  onChange={(e) => updateStatus(p.id, e.target.value)}
                  className="text-xs px-2 py-1.5 rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-slate-300"
                  data-testid={`prospect-status-${p.id}`}
                >
                  {STATUS_OPTS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </td>
              <td className="px-4 py-2.5">
                <input
                  key={`demo-${p.id}-${p.demo_at || ""}`}
                  type="datetime-local"
                  defaultValue={toDatetimeLocalValue(p.demo_at)}
                  onChange={(e) => updateDemoAt(p.id, e.target.value)}
                  className="text-xs px-2 py-1.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300"
                  data-testid={`prospect-demo-${p.id}`}
                />
              </td>
              <td className="px-4 py-2.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => openNotes(p)}
                    className="text-xs px-3 py-1.5 rounded-md border border-slate-200 hover:border-slate-400"
                    data-testid={`prospect-notes-${p.id}`}
                  >
                    Notes
                  </button>
                  {p.demo_tenant_id ? (
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full border bg-slate-50 text-slate-600 border-slate-200">
                        Sandbox until {fmtDateOnly(p.demo_tenant_expires_at)}
                      </span>
                      <Link to="/app/tenants" className="text-xs text-[#005E82] hover:underline">
                        Open
                      </Link>
                    </div>
                  ) : (
                    <button
                      onClick={() => openDemo(p)}
                      className="text-xs px-3 py-1.5 rounded-md border border-slate-200 hover:border-slate-400"
                      data-testid={`prospect-demo-${p.id}`}
                    >
                      Create demo
                    </button>
                  )}
                </div>
              </td>
            </tr>
          );
        })}
      </TableShell>

      <Drawer
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add prospect"
        footer={
          <>
            <button onClick={() => setAddOpen(false)} className="text-sm px-3 py-2 rounded-md border border-slate-200">
              Cancel
            </button>
            <button onClick={saveProspect} disabled={saving} className="btn-primary" data-testid="prospect-save">
              Save
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Name" testId="prospect-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Field
            label="Company"
            testId="prospect-form-company"
            value={form.company}
            onChange={(e) => setForm({ ...form, company: e.target.value })}
          />
          <Field
            label="Email"
            testId="prospect-form-email"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <Field label="Phone" testId="prospect-form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <Field
            label="Fleet size"
            testId="prospect-form-fleet"
            value={form.fleet_size}
            onChange={(e) => setForm({ ...form, fleet_size: e.target.value })}
            options={FLEET_OPTS}
          />
          <Field
            label="What do they use today"
            testId="prospect-form-tools"
            value={form.current_tools}
            onChange={(e) => setForm({ ...form, current_tools: e.target.value })}
          />
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Message</label>
            <textarea
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={3}
              data-testid="prospect-form-message"
              className="mt-1 w-full text-sm px-3 py-2.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300"
            />
          </div>
        </div>
      </Drawer>

      <Drawer
        open={notesOpen}
        onClose={() => setNotesOpen(false)}
        title="Notes"
        footer={
          <>
            <button onClick={() => setNotesOpen(false)} className="text-sm px-3 py-2 rounded-md border border-slate-200">
              Close
            </button>
            <button onClick={saveNotes} disabled={savingNotes} className="btn-primary" data-testid="prospect-notes-save">
              Save
            </button>
          </>
        }
      >
        {notesRow && (
          <div className="space-y-4">
            <div className="text-sm text-slate-900 font-medium">
              {notesRow.name}
              {notesRow.company ? ` - ${notesRow.company}` : ""}
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Message from the form</label>
              <div className="mt-1 w-full text-sm px-3 py-2.5 rounded-md border border-slate-200 bg-slate-50 text-slate-700 whitespace-pre-wrap min-h-[64px]">
                {notesRow.message || <span className="text-slate-400">No message.</span>}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Notes</label>
              <textarea
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                rows={6}
                className="mt-1 w-full text-sm px-3 py-2.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300"
              />
            </div>
            <div className="text-xs text-slate-400">
              {notesRow.updated_by_email
                ? `Last updated ${fmtWhen(notesRow.updated_at)} by ${notesRow.updated_by_email}`
                : "No updates yet."}
            </div>
          </div>
        )}
      </Drawer>

      <DemoTenantDrawer
        open={demoOpen}
        onClose={() => setDemoOpen(false)}
        prospect={demoProspect}
        onCreated={load}
      />
    </div>
  );
}
